'use client'

import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Multi-photo feed gallery (2–5 photos). A horizontal scroll-snap strip gives native swipe on
 * touch + trackpad, with dot indicators tracking the active slide; tapping any photo opens a
 * full-screen lightbox with prev/next. A SINGLE photo is handled by the caller (rendered "as today"),
 * so this component is only used for 2+ photos. No external deps.
 */
export default function FeedGallery({ photos }: { photos: string[] }) {
  const stripRef = useRef<HTMLDivElement | null>(null)
  const [active, setActive] = useState(0)
  const [full, setFull] = useState<number | null>(null)

  const onScroll = useCallback(() => {
    const el = stripRef.current
    if (!el) return
    const i = Math.round(el.scrollLeft / Math.max(el.clientWidth, 1))
    setActive(Math.min(Math.max(i, 0), photos.length - 1))
  }, [photos.length])

  // Lightbox keyboard nav (arrows + escape).
  useEffect(() => {
    if (full === null) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setFull(null)
      else if (e.key === 'ArrowRight') setFull((f) => (f === null ? f : Math.min(f + 1, photos.length - 1)))
      else if (e.key === 'ArrowLeft') setFull((f) => (f === null ? f : Math.max(f - 1, 0)))
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [full, photos.length])

  return (
    <div className="fg">
      <div className="fg-strip" ref={stripRef} onScroll={onScroll}>
        {photos.map((p, i) => (
          <button type="button" className="fg-slide" key={i} onClick={() => setFull(i)} aria-label={`Open photo ${i + 1} of ${photos.length}`}>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={p} alt="" loading="lazy" />
          </button>
        ))}
      </div>
      <div className="fg-dots" aria-hidden="true">
        {photos.map((_, i) => <span key={i} className={i === active ? 'on' : ''} />)}
      </div>
      <span className="fg-count" aria-hidden="true">{active + 1}/{photos.length}</span>

      {full !== null && (
        <div className="fg-lb" role="dialog" aria-modal="true" onClick={() => setFull(null)}>
          <button type="button" className="fg-lb-x" onClick={() => setFull(null)} aria-label="Close">×</button>
          {full > 0 && (
            <button type="button" className="fg-lb-nav prev" onClick={(e) => { e.stopPropagation(); setFull(full - 1) }} aria-label="Previous">‹</button>
          )}
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src={photos[full]} alt="" onClick={(e) => e.stopPropagation()} />
          {full < photos.length - 1 && (
            <button type="button" className="fg-lb-nav next" onClick={(e) => { e.stopPropagation(); setFull(full + 1) }} aria-label="Next">›</button>
          )}
          <span className="fg-lb-count">{full + 1} / {photos.length}</span>
        </div>
      )}

      <style jsx>{`
        .fg { position: relative; margin-top: 11px; }
        .fg-strip {
          display: flex; overflow-x: auto; scroll-snap-type: x mandatory;
          border-radius: 12px; -webkit-overflow-scrolling: touch; scrollbar-width: none;
          background: #eceae2;
        }
        .fg-strip::-webkit-scrollbar { display: none; }
        .fg-slide {
          flex: 0 0 100%; scroll-snap-align: center; padding: 0; border: 0; background: none;
          cursor: zoom-in; display: block;
        }
        .fg-slide img { width: 100%; height: 240px; object-fit: cover; display: block; }
        .fg-dots {
          position: absolute; left: 0; right: 0; bottom: 10px; display: flex; gap: 6px;
          justify-content: center; pointer-events: none;
        }
        .fg-dots span {
          width: 6px; height: 6px; border-radius: 50%; background: rgba(255,255,255,0.55);
          box-shadow: 0 0 2px rgba(0,0,0,0.4); transition: background 0.2s, width 0.2s;
        }
        .fg-dots span.on { background: #fff; width: 16px; border-radius: 3px; }
        .fg-count {
          position: absolute; top: 10px; right: 10px; font-size: 12px; font-weight: 600;
          color: #fff; background: rgba(0,0,0,0.5); padding: 2px 8px; border-radius: 999px;
        }
        .fg-lb {
          position: fixed; inset: 0; z-index: 1000; background: rgba(0,0,0,0.92);
          display: flex; align-items: center; justify-content: center;
        }
        .fg-lb img { max-width: 94vw; max-height: 88vh; object-fit: contain; border-radius: 6px; }
        .fg-lb-x {
          position: absolute; top: 16px; right: 20px; font-size: 34px; line-height: 1;
          color: #fff; background: none; border: 0; cursor: pointer;
        }
        .fg-lb-nav {
          position: absolute; top: 50%; transform: translateY(-50%); font-size: 48px; line-height: 1;
          color: #fff; background: none; border: 0; cursor: pointer; padding: 0 16px;
        }
        .fg-lb-nav.prev { left: 4px; }
        .fg-lb-nav.next { right: 4px; }
        .fg-lb-count {
          position: absolute; bottom: 20px; left: 0; right: 0; text-align: center;
          color: #fff; font-size: 14px; font-weight: 600;
        }
      `}</style>
    </div>
  )
}
